/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Browser and platform detection.
 *
 * Kept to the two questions the Map Viewer actually asks — whether it is
 * Chrome, and whether the reader is on a Mac, which decides how a keyboard
 * shortcut is named — plus the transition-end event name.
 */

let cached = null;

/**
 * Describe the browser and platform.
 *
 * Computed on first call rather than at import: the feature probe builds an
 * element, so the answer depends on when the bundle happens to run. The
 * result does not change during a page's life, so it is remembered.
 *
 * @returns {{isChrome: boolean, isMac: boolean,
 *   transitionEndEvent: string|undefined}}
 */
export function browser() {
    if (cached) return cached;
    cached = {
        isChrome:
            (navigator.userAgentData &&
            Array.isArray(navigator.userAgentData.brands)
                ? navigator.userAgentData.brands.some((b) =>
                      /Chrome/i.test(b.brand)
                  )
                : /\bChrome\//.test(navigator.userAgent)) &&
            (navigator.vendor || '').indexOf('Google') > -1,
        isMac: navigator.userAgentData?.platform
            ? navigator.userAgentData.platform.toLowerCase() === 'macos'
            : /\bMac\b/i.test(navigator.userAgent),
        transitionEndEvent: (() => {
            const el = document.createElement('fakeelement');
            const transitions = {
                transition: 'transitionend',
                OTransition: 'oTransitionEnd',
                MozTransition: 'transitionend',
                WebkitTransition: 'webkitTransitionEnd'
        };

        let t;
        for (t in transitions) {
            if (el.style[t] !== undefined) {
                return transitions[t];
            }
        }
    })()
    };
    return cached;
}

/** Forget the cached result. Intended for tests. */
export function resetBrowser() {
    cached = null;
}

const VENDOR_PREFIXES = ['-webkit-', '-moz-', '-ms-', '-o-'];
const prefixCache = {};

/**
 * Return the CSS property name this browser understands.
 *
 * Answers with the unprefixed name where it is supported, and the first
 * vendor-prefixed spelling that is otherwise. The answer is remembered per
 * property, because it cannot change during a page's life.
 *
 * @param {string} attr - The unprefixed property name.
 * @returns {string|undefined} The usable name, or nothing if none is.
 */
export function getVendorCSS(attr) {
    if (prefixCache[attr] === undefined) {
        if (attr in document.body.style) {
            prefixCache[attr] = attr;
        } else {
            VENDOR_PREFIXES.some((prefix) => {
                const prefixed = prefix + attr;
                if (prefixed in document.body.style) {
                    prefixCache[attr] = prefixed;
                    return true;
                }
                return false;
            });
        }
    }
    return prefixCache[attr];
}
